import { signOut } from "firebase/auth";
import { reportSignOutFailed } from "../../../../shared/errorReporting";
import { safeStorage, type SafeStorageArea } from "../../../../shared/lib/safeStorage";
import { getFirebaseAuth, resumeCredentialedRequests, suppressCredentialedRequests } from "../lib/firebase";

/**
 * SIGN THE PERSON OUT WITHOUT EVER STRANDING THEM (sign-out increment,
 * Director-approved 2026-10-07). Used by a SESSION END (IdentityProvider).
 * The person's own Sign out is auth/deliberateSignOut.ts: it waits for
 * Firebase and never claims a sign-out that did not happen. A session end
 * that lands while one of those is under way runs here on its own; both
 * route, the last navigation wins, and the deliberate line is the one kept
 * (shared/hooks/useSignOutNotice.ts).
 *
 * WHY SIGN-OUT FAILS IN NORMAL USE. Firebase Auth 1.13's IndexedDB
 * persistence closes its database whenever the document is hidden
 * (visibilitychange to hidden, or pagehide) and refuses every write until it
 * is visible again: `signOut` rejects with "Database is closing/hidden". A
 * session that ends in a background tab, during a phone app switch or from
 * the back/forward cache meets exactly that. Storage pressure and private
 * modes can refuse it too. Before this, a rejected sign-out after a session
 * end was only logged: the person saw a generic load error while the revoked
 * user stayed current (rc.3 joint session, 2026-10-07).
 *
 * WHAT HAPPENS NOW:
 * 1. Credentialed requests for this uid stop at once (lib/firebase.ts
 *    `suppressCredentialedRequests`), so a revoked session is not sprayed with
 *    refusals while the SDK still holds it.
 * 2. Sign out. On success, route and finish.
 * 3. On failure, route ANYWAY (the sign-in or service screen with its line),
 *    after telling the page (`onRefused`) to stop treating the uid as
 *    signed in: the SDK still holds it, and a screen that read it would
 *    send a "signed-in" client from sign-in straight back into the flow.
 *    In a hidden tab, retry once on the next visible moment, which is when
 *    Firebase can write again: the normal case, not reported, no reload.
 * 4. If it still fails with the tab visible, report one fixed warning and,
 *    unless someone else is signed in by now, do ONE hard reload to `reloadTo` so the
 *    revoked identity cannot keep making requests from this page. A per-tab
 *    marker (session storage) allows that reload once: a failure after it,
 *    or with no storage to hold the marker, stays on the routed screen and
 *    never loops.
 */

export const SIGN_OUT_RELOAD_MARKER = "lura.book.signOutReload";

export type SignOutOutcome = "signed-out" | "signed-out-when-visible" | "reloaded" | "failed";

export interface SignOutSafelyOptions {
  /** The uid being signed out. */
  uid: string;
  /** Takes the person to where they belong now. Called exactly once. */
  route(): void;
  /**
   * Firebase refused the first attempt, so the SDK still holds this uid.
   * Called before `route`, so the page stops treating it as signed in.
   */
  onRefused?(): void;
  /** Same-origin path for the one safety reload. */
  reloadTo: string;
  // Injected by tests.
  doc?: Document;
  win?: Window;
  storage?: SafeStorageArea;
  signOutNow?(): Promise<void>;
  currentUid?(): string | null;
  report?(): void;
}

function whenVisible(doc: Document): Promise<void> {
  return new Promise((resolve) => {
    const onChange = () => {
      if (doc.visibilityState !== "visible") return;
      doc.removeEventListener("visibilitychange", onChange);
      resolve();
    };
    doc.addEventListener("visibilitychange", onChange);
  });
}

/**
 * ONE SIGN-OUT PER UID AT A TIME. While a refused sign-out waits for the tab
 * to be visible, a request already on the wire can still come back
 * SESSION_REVOKED and end the same session again; and a session end can land
 * after the person pressed Sign out. Either joins the sign-out under way: it
 * has routed once already, and a deliberate sign-out's line is the one kept.
 */
let underWay: { uid: string; outcome: Promise<SignOutOutcome> } | null = null;

export function signOutSafely(options: SignOutSafelyOptions): Promise<SignOutOutcome> {
  if (underWay?.uid === options.uid) return underWay.outcome;
  const outcome = run(options);
  const entry = { uid: options.uid, outcome };
  underWay = entry;
  void outcome.finally(() => {
    if (underWay === entry) underWay = null;
  });
  return outcome;
}

/** Test seam: forget a sign-out still waiting for a visible tab. */
export function __resetSignOutSafelyForTests(): void {
  underWay = null;
}

async function run(options: SignOutSafelyOptions): Promise<SignOutOutcome> {
  const doc = options.doc ?? document;
  const win = options.win ?? window;
  const signOutNow = options.signOutNow ?? (() => signOut(getFirebaseAuth()));
  const currentUid = options.currentUid ?? (() => getFirebaseAuth().currentUser?.uid ?? null);
  const report = options.report ?? reportSignOutFailed;
  const storage = options.storage ?? safeStorage.session;
  const finish = () => {
    resumeCredentialedRequests();
    storage.remove(SIGN_OUT_RELOAD_MARKER);
  };

  suppressCredentialedRequests(options.uid);
  try {
    await signOutNow();
    finish();
    options.route();
    return "signed-out";
  } catch {
    options.onRefused?.();
    options.route();
  }

  if (doc.visibilityState === "hidden") {
    await whenVisible(doc);
    try {
      await signOutNow();
      finish();
      return "signed-out-when-visible";
    } catch {
      /* visible and still refused: the safety reload below */
    }
  }

  report();
  // Only once the marker is stored: storage that cannot hold it could not
  // stop a second reload either, so there is none.
  // Firebase 1.13 drops the user from MEMORY before the refused write, so
  // `currentUid()` is usually null here while storage still holds them, and
  // a reload would restore them. So the reload happens unless someone ELSE
  // is signed in now; the next load's fresh SDK signs the ended session out.
  const now = currentUid();
  if (
    (now === null || now === options.uid) &&
    storage.get(SIGN_OUT_RELOAD_MARKER) !== "1" &&
    storage.set(SIGN_OUT_RELOAD_MARKER, "1")
  ) {
    win.location.replace(options.reloadTo);
    return "reloaded";
  }
  return "failed";
}
